<template>
  <div class="now-playing-now-playing">
    <div class="now-playing-header">
      <span class="now-playing-track" v-once>NOW PLAYING</span>
    </div>
    <div class="now-playing-playing-track">
      <div class="now-playing-artwork">
        <div class="now-playing-image-container" :style="coverBackground" :class="{ 'fade-in': imageLoaded }">
          <div class="cover-button" :class="{ 'play-control': !isPlaying, 'pause-control': isPlaying, 'error-state': connectionError }" @click="controlPlayer()"></div>
          <div v-if="connectionError" class="connection-error-indicator">
            <span>Connection lost, reload the page</span>
          </div>
          <span class="hidden">
                <audio v-if="isIOS || !hlsIsSupported" ref="radioPlayer" :title="videoTitle"></audio>
                <video v-else :poster="imageUrl" :title="videoTitle" ref="radioPlayer" playsinline></video>
            </span>
        </div>
      </div>
      <div class="now-playing-info">
        <div class="now-playing-text">
          <h1 class="now-playing-artist">{{ artist }}</h1>
          <span class="now-playing-title">{{ title }}</span>
          <span class="now-playing-album"><span v-if="year !== null">{{year}} - </span>{{ album }}</span>
        </div>
        <track-icons
          class="now-playing-icons"
          :favorited="favorited"
          :discogsUrl="discogsUrl"
          :trackTitle="title"
          :trackArtist="artist"
          @toggle-favorite="onToggleFavorite"
          @open-signup="$emit('open-signup')"
        />
      </div>
    </div>
  </div>
</template>

<script>
import Hls from 'hls.js';
import TrackIcons from './track-icons.vue';

export default {
  name: 'NowPlaying',
  components: {TrackIcons},
  props: {
    artist: {
      type: String,
      default: 'ARTIST',
    },
    title: {
      type: String,
      default: 'TITLE',
    },
    album: {
      type: String,
      default: 'ALBUM',
    },
    year: {
      type: String,
      default: null,
    },
    discogsUrl: {
      type: String,
      default: null,
    },
    favorited: {
      type: Boolean,
      default: false,
    },
    imageUrl: {
      type: String,
      default: null,
    }
  },
  data () {
    return {
      isPlaying: false,
      streamUrl: this.getAdaptiveStreamUrl(),
      renderTime: new Date(),
      minButtonOpacity: .1,
      buttonOpacity: .7,
      buttonOpacityStyle: "opacity: .7;",
      hlsInstance: null,
      buttonOpacityInterval: null,
      connectionError: false,
      reconnectAttempts: 0,
      maxReconnectAttempts: 5,
      reconnectInterval: null,
      imageLoaded: false,
      currentImageUrl: null
    };
  },
  methods: {
    getAdaptiveStreamUrl() {
      // Use lower bitrate on mobile to prevent overheating
      const isMobile = /Android|iPhone|iPad/.test(navigator.userAgent);
      if (isMobile) {
        return "https://admin.nuance.radio/hls/nuance_radio/aac_lofi.m3u8"; // 96k for mobile
      }
      return "https://admin.nuance.radio/hls/nuance_radio/aac_hifi.m3u8"; // 192k for desktop
    },
    
    getMobileOptimizedHlsConfig() {
      const isMobile = /Android|iPhone|iPad/.test(navigator.userAgent);
      
      if (isMobile) {
        return {
          // Conservative settings for mobile to reduce CPU usage
          maxBufferLength: 10, // Reduced from 30
          maxMaxBufferLength: 20, // Reduced from 60
          maxBufferSize: 20 * 1000 * 1000, // 20MB instead of 60MB
          maxBufferHole: 0.1, // Reduced from 0.5
          lowLatencyMode: false,
          backBufferLength: 30, // Reduced from 90
          enableWorker: false, // Disable worker on mobile to reduce overhead
          debug: false,
          // Additional mobile optimizations
          maxFragLookUpTolerance: 0.1,
          maxStarvationDelay: 2,
          maxLoadingDelay: 2,
          // Reduce quality switching frequency
          abrEwmaDefaultEstimate: 500000, // 500kbps default
          abrBandWidthFactor: 0.95,
          abrBandWidthUpFactor: 0.7,
          // Disable features that consume CPU
          enableSoftwareAES: false,
          enableDateRangeMetadataCues: false,
          enableEmsgMetadataCues: false,
          enableID3MetadataCues: false,
          enableWebVTT: false,
          enableIMSC1: false,
          enableCEA708Captions: false,
          enableEmbeddedSubtitles: false
        };
      }
      
      return {
        // Desktop settings
        maxBufferLength: 30,
        maxMaxBufferLength: 60,
        maxBufferSize: 60 * 1000 * 1000,
        maxBufferHole: 0.5,
        lowLatencyMode: false,
        backBufferLength: 90,
        enableWorker: true,
        debug: false
      };
    },
    
    getFallbackStreamUrl() {
      // Fallback to midfi if lofi fails, then to hifi
      const isMobile = /Android|iPhone|iPad/.test(navigator.userAgent);
      if (isMobile) {
        if (this.streamUrl.includes('aac_lofi')) {
          return "https://admin.nuance.radio/hls/nuance_radio/aac_midfi.m3u8"; // 128k fallback
        } else if (this.streamUrl.includes('aac_midfi')) {
          return "https://admin.nuance.radio/hls/nuance_radio/aac_hifi.m3u8"; // 192k final fallback
        }
      }
      return "https://admin.nuance.radio/hls/nuance_radio/aac_midfi.m3u8"; // Desktop fallback
    },
    
    handleHlsError(error, fatal) {
      console.error('HLS Error:', error, 'Fatal:', fatal);
      
      if (fatal) {
        this.connectionError = true;
        this.isPlaying = false;
        this.$emit("audioPlaying", false);
        
        // Try to reconnect with fallback stream
        if (this.reconnectAttempts < this.maxReconnectAttempts) {
          this.reconnectAttempts++;
          console.log(`Attempting reconnection ${this.reconnectAttempts}/${this.maxReconnectAttempts}`);
          
          // Try fallback stream
          this.streamUrl = this.getFallbackStreamUrl();
          
          setTimeout(() => {
            this.attemptReconnection();
          }, 2000 * this.reconnectAttempts); // Exponential backoff
        } else {
          console.error('Max reconnection attempts reached');
          this.showReconnectButton();
        }
      }
    },
    
    handleHlsRecovered() {
      console.log('HLS stream recovered');
      this.connectionError = false;
      this.reconnectAttempts = 0;
      this.isPlaying = true;
      this.$emit("audioPlaying", true);
    },
    
    attemptReconnection() {
      if (this.hlsInstance) {
        this.hlsInstance.destroy();
      }
      
      let player = this.$refs["radioPlayer"];
      if (!player) return;
      
      this.hlsInstance = new Hls(this.getMobileOptimizedHlsConfig());
      
      // Add error handling
      this.hlsInstance.on(Hls.Events.ERROR, this.handleHlsError);
      this.hlsInstance.on(Hls.Events.MANIFEST_LOADED, this.handleHlsRecovered);
      
      this.hlsInstance.loadSource(this.streamUrl);
      this.hlsInstance.attachMedia(player);
      
      player.play().catch(error => {
        console.error('Playback failed after reconnection:', error);
        this.connectionError = true;
        this.isPlaying = false;
        this.$emit("audioPlaying", false);
      });
    },
    
    showReconnectButton() {
      this.connectionError = true;
      this.isPlaying = false;
      this.$emit("audioPlaying", false);
      
      // On mobile, auto-reload the page after a short delay to restart the stream
      const isMobile = /Android|iPhone|iPad/.test(navigator.userAgent);
      if (isMobile) {
        console.log('Max reconnection attempts reached on mobile, reloading page...');
        // Store that we should auto-play after reload
        sessionStorage.setItem('nuanceAutoPlay', 'true');
        setTimeout(() => {
          window.location.reload();
        }, 3000); // 3 second delay to show the message
      }
      // The play button will be visible again automatically on desktop
    },
    controlPlayer() {
      let player = this.$refs["radioPlayer"];
      console.log('controlPlayer called, current isPlaying:', this.isPlaying);
      
      if (!this.isPlaying) {
        // Starting playback
        this.connectionError = false;
        this.reconnectAttempts = 0;
        
        // Check if we're on mobile
        const isMobile = /Android|iPhone|iPad/.test(navigator.userAgent);
        
        if (this.hlsInstance) {
          if (isMobile) {
            // Mobile: Use actual pause/play for proper MediaSession support
            console.log('Mobile: Resuming playback with actual play()');
            
            // Re-attach HLS media to ensure stream resumes
            try {
              console.log('Re-attaching HLS media to player');
              this.hlsInstance.attachMedia(player);
              
              // Use actual play() method for mobile
              console.log('Calling player.play() to resume stream');
              player.play().then(() => {
                console.log('Player.play() succeeded');
              }).catch(error => {
                console.error('Resume playback failed:', error);
                this.connectionError = true;
                this.isPlaying = false;
                this.$emit("audioPlaying", false);
              });
            } catch (error) {
              console.error('HLS re-attach failed:', error);
              // Fallback: try to restart the stream
              this.restartStream();
            }
          } else {
            // Desktop: Use mute/unmute for better performance
            console.log('Desktop: Resuming playback by unmuting');
            player.muted = false;
            player.volume = 1.0;
          }
        } else {
          // Fresh start - initialize stream
          this.streamUrl = this.getAdaptiveStreamUrl();
          
        if (this.hlsIsSupported) {
          if (this.hlsInstance) {
            this.hlsInstance.destroy();
          }
            this.hlsInstance = new Hls(this.getMobileOptimizedHlsConfig());
            
            // Add error handling
            this.hlsInstance.on(Hls.Events.ERROR, this.handleHlsError);
            this.hlsInstance.on(Hls.Events.MANIFEST_LOADED, this.handleHlsRecovered);
            
          this.hlsInstance.loadSource(this.streamUrl);
          this.hlsInstance.attachMedia(player);
        } else {
          player.src = this.streamUrl;
        }
        player.muted = false;
        player.volume = 1.0;
          player.play().catch(error => {
            console.error('Playback failed:', error);
            this.connectionError = true;
            this.isPlaying = false;
            this.$emit("audioPlaying", false);
          });
        }
        
        this.isPlaying = true;
        // Update MediaSession state to playing
        if (navigator.mediaSession) {
          navigator.mediaSession.playbackState = 'playing';
        }
        this.setMediaSession(); // Set after playback starts
        
        // Start button opacity animation only when clicked
        this.buttonOpacity = 0.7;
        this.renderTime = new Date();
        if (this.buttonOpacityInterval) {
          clearInterval(this.buttonOpacityInterval);
        }
        this.buttonOpacityInterval = setInterval(this.computeButtonOpacity, 50); // Faster animation for better UX
      } else {
        // Pausing playback
        const isMobile = /Android|iPhone|iPad/.test(navigator.userAgent);
        
        if (isMobile) {
          // Mobile: Use actual pause for proper MediaSession support
          console.log('Mobile: Pausing playback with actual pause()');
          if (player) {
            player.pause();
          }
        } else {
          // Desktop: Use mute for better performance
          console.log('Desktop: Pausing playback by muting');
          if (player) {
            player.muted = true;
          }
        }
        
        this.isPlaying = false;
        this.connectionError = false;
        
        // Update MediaSession state to paused
        if (navigator.mediaSession) {
          navigator.mediaSession.playbackState = 'paused';
        }
      }
      this.$emit("audioPlaying", this.isPlaying)
    },
    computeButtonOpacity() {
      // Only animate opacity when button is clicked, not continuously
      if (this.isPlaying) {
        let now = new Date();
        let delta = (now.getTime() - this.renderTime.getTime()) / 1000000;
        this.buttonOpacity -= delta;
        if (this.buttonOpacity >= 0){
          this.buttonOpacityStyle = `opacity: ${this.buttonOpacity};`;
        } else {
          // Stop the interval when animation is complete
          if (this.buttonOpacityInterval) {
            clearInterval(this.buttonOpacityInterval);
            this.buttonOpacityInterval = null;
          }
        };
      } else {
        this.buttonOpacityStyle = "opacity: .7;"
        // Stop the interval when not playing
        if (this.buttonOpacityInterval) {
          clearInterval(this.buttonOpacityInterval);
          this.buttonOpacityInterval = null;
        }
      }
    },
    setMediaSession() {
      if ('mediaSession' in navigator) {
        // Create a longer title that will "uncoil" on Android lock screens
        const longTitle = `Nuance Radio - ${this.artist || 'Unknown Artist'} - ${this.title}`;
        
        navigator.mediaSession.metadata = new MediaMetadata({
          title: longTitle,
          artist: this.artist || 'Nuance Radio',
          album: this.album || 'Nuance Radio',
          artwork: [
            { src: this.imageUrl, sizes: '512x512', type: 'image/jpeg' }
          ]
        });
        
        // Set the app name for better notification display
        if (navigator.mediaSession.setActionHandler) {
                  // Set playback state based on current playing state
        navigator.mediaSession.playbackState = this.isPlaying ? 'playing' : 'paused';
          
          // Handle play action from lock screen/notification
          navigator.mediaSession.setActionHandler('play', () => {
            console.log('MediaSession: Play action triggered, current isPlaying:', this.isPlaying);
            
            // Check if player is actually paused (mobile) or muted (desktop)
            const player = this.$refs["radioPlayer"];
            const isMobile = /Android|iPhone|iPad/.test(navigator.userAgent);
            const isPaused = isMobile ? (player && player.paused) : (player && player.muted);
            
            if (!this.isPlaying || isPaused) {
              console.log('Calling controlPlayer from MediaSession play handler');
              
              // Set MediaSession state immediately to playing
              if (navigator.mediaSession) {
                navigator.mediaSession.playbackState = 'playing';
              }
              
              // Then call controlPlayer
              this.controlPlayer();
            } else {
              console.log('Already playing, ignoring play action');
            }
          });
          
          // Handle pause action from lock screen/notification
          navigator.mediaSession.setActionHandler('pause', () => {
            console.log('MediaSession: Pause action triggered, current isPlaying:', this.isPlaying);
            
            // Check if player is actually paused (mobile) or muted (desktop)
            const player = this.$refs["radioPlayer"];
            const isMobile = /Android|iPhone|iPad/.test(navigator.userAgent);
            const isPaused = isMobile ? (player && player.paused) : (player && player.muted);
            
            if (this.isPlaying && !isPaused) {
              console.log('Calling controlPlayer from MediaSession pause handler');
              
              // Set MediaSession state immediately to paused
              if (navigator.mediaSession) {
                navigator.mediaSession.playbackState = 'paused';
              }
              
              // Then call controlPlayer
              this.controlPlayer();
            } else {
              console.log('Already paused, ignoring pause action');
            }
          });
          
          // Handle stop action from lock screen/notification
          navigator.mediaSession.setActionHandler('stop', () => {
            console.log('MediaSession: Stop action triggered');
            if (this.isPlaying) {
              this.controlPlayer();
            }
          });
          
          // Handle seekbackward action (optional)
          navigator.mediaSession.setActionHandler('seekbackward', () => {
            console.log('MediaSession: Seek backward action triggered');
            // Could be used for previous track if needed
          });
          
          // Handle seekforward action (optional)
          navigator.mediaSession.setActionHandler('seekforward', () => {
            console.log('MediaSession: Seek forward action triggered');
            // Could be used for next track if needed
          });
        }
      }
    },
    onToggleFavorite() {
      this.$emit('toggle-favorite');
    },
    handleNetworkChange() {
      // If network improves and we have connection error, try to reconnect
      if (this.connectionError && navigator.connection.effectiveType !== 'slow-2g') {
        console.log('Network improved, attempting reconnection');
        this.attemptReconnection();
      }
    },
    
    handleVisibilityChange() {
      // When page becomes visible again, check if we need to reconnect
      if (!document.hidden && this.connectionError && this.isPlaying) {
        console.log('Page became visible, checking connection');
        setTimeout(() => {
          this.attemptReconnection();
        }, 1000);
      }
    },
    

    
    restartStream() {
      console.log('Restarting stream as fallback');
      this.showDebugMessage('Restarting stream as fallback');
      
      const player = this.$refs["radioPlayer"];
      if (this.hlsInstance) {
        this.hlsInstance.destroy();
        this.hlsInstance = null;
      }
      
      // Reinitialize the stream
      this.streamUrl = this.getAdaptiveStreamUrl();
      
      if (this.hlsIsSupported) {
        this.hlsInstance = new Hls(this.getMobileOptimizedHlsConfig());
        this.hlsInstance.on(Hls.Events.ERROR, this.handleHlsError);
        this.hlsInstance.on(Hls.Events.MANIFEST_LOADED, this.handleHlsRecovered);
        this.hlsInstance.loadSource(this.streamUrl);
        this.hlsInstance.attachMedia(player);
      } else {
        player.src = this.streamUrl;
      }
      
      player.muted = false;
      player.volume = 1.0;
      player.play().catch(error => {
        console.error('Stream restart failed:', error);
        this.connectionError = true;
        this.isPlaying = false;
        this.$emit("audioPlaying", false);
      });
    },
    
    forceMediaSessionUpdate() {
      if (navigator.mediaSession) {
        // Force a complete MediaSession refresh
        const currentState = navigator.mediaSession.playbackState;
        const newState = this.isPlaying ? 'playing' : 'paused';
        
        console.log('Force MediaSession update:', currentState, '->', newState);
        
        // Set the state
        navigator.mediaSession.playbackState = newState;
        
        // Force a metadata update to trigger UI refresh
        if (navigator.mediaSession.metadata) {
          const metadata = navigator.mediaSession.metadata;
          navigator.mediaSession.metadata = new MediaMetadata({
            title: metadata.title,
            artist: metadata.artist,
            album: metadata.album,
            artwork: metadata.artwork
          });
        }
        
        // Force Android to recognize the state change by temporarily clearing and re-setting
        setTimeout(() => {
          if (navigator.mediaSession) {
            const tempState = navigator.mediaSession.playbackState;
            navigator.mediaSession.playbackState = 'none';
            setTimeout(() => {
              navigator.mediaSession.playbackState = newState;
            }, 10);
          }
        }, 100);
      }
    },
  },
  watch: {
    imageUrl: {
      handler(newUrl, oldUrl) {
        if (newUrl !== oldUrl) {
          this.imageLoaded = false;
          this.currentImageUrl = newUrl;
          
          // Trigger fade-in after a short delay to ensure new image is loaded
          setTimeout(() => {
            this.imageLoaded = true;
          }, 100);
        }
      },
      immediate: true
    },
    // whenever track title changes, this function will run
    title(newTitle, oldTitle) {
      this.setMediaSession();
    }
  },
  computed: {
    coverBackground() {
      return this.imageUrl ? `background-image:url('${this.imageUrl}')` : 'background-color: white';
    },
    hlsIsSupported() {
      return Hls.isSupported()
    },
    videoTitle() {
      return `Nuance Radio: ${this.artist} - ${this.title} `
    },
    isIOS() {
      return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    }
  },
  mounted() {
    // Button opacity animation is now triggered only on click, not continuously
    this.setMediaSession();
    
    // Listen for network changes on mobile
    if ('connection' in navigator) {
      navigator.connection.addEventListener('change', this.handleNetworkChange);
    }
    
    // Listen for page visibility changes
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
    
    // Auto-play after reload if it was set in sessionStorage (from connection error recovery)
    const shouldAutoPlay = sessionStorage.getItem('nuanceAutoPlay');
    if (shouldAutoPlay === 'true') {
      sessionStorage.removeItem('nuanceAutoPlay');
      // Small delay to ensure component is fully ready
      setTimeout(() => {
        const isMobile = /Android|iPhone|iPad/.test(navigator.userAgent);
        if (isMobile && !this.isPlaying) {
          console.log('Auto-playing stream after reload...');
          this.controlPlayer();
        }
      }, 500);
    }
  },
  
  beforeDestroy() {
    // Clean up intervals and timeouts
    if (this.buttonOpacityInterval) {
      clearInterval(this.buttonOpacityInterval);
      this.buttonOpacityInterval = null;
    }
    if (this.reconnectInterval) {
      clearTimeout(this.reconnectInterval);
      this.reconnectInterval = null;
    }
    
    // Clean up HLS instance
    if (this.hlsInstance) {
      this.hlsInstance.destroy();
      this.hlsInstance = null;
    }
    
    // Remove event listeners
    if ('connection' in navigator) {
      navigator.connection.removeEventListener('change', this.handleNetworkChange);
    }
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    
    // Clear references to prevent memory leaks
    this.streamUrl = null;
    this.renderTime = null;
  }
}
</script>

<style scoped>
.now-playing-now-playing {
  width: 100%;
  height: 100%;
  position: relative;
  margin-right: var(--dl-space-space-fourunits);
}
.now-playing-header {
  top: 0px;
  left: 0px;
  display: flex;
  position: absolute;
  align-items: flex-start;
  flex-direction: column;
  justify-content: flex-start;
  z-index: 1000;
}
.now-playing-track {
  color: #ff0000;
  font-size: var(--played) !important;
  align-self: center;
  font-style: normal;
  font-family: Syne;
  line-height: 1;
  padding: 4px 4px 2px 4px;
  background-color: #ffffff;
}
.now-playing-playing-track {
  width: 100%;
  display: flex;
  align-self: center;
  margin-right: var(--dl-space-space-sixunits);
  flex-direction: column;
  justify-content: flex-start;
  background-color: #ffffff;
}
.now-playing-artwork {
  width: 100%;
  display: flex;
  padding: 4px;
  flex-direction: column;
  justify-content: flex-start;
}
.now-playing-image-container {
  position: relative;
  width: 100%;
  padding-bottom: 100%;
  background-size: cover;
  /* GPU acceleration for better performance */
  transform: translateZ(0);
  will-change: transform;
  transition: opacity 0.5s ease-in-out;
  opacity: 0;
}

.now-playing-image-container.fade-in {
  opacity: 1;
}
.now-playing-image {
  width: 70%;
  cursor: pointer;
  object-fit: cover;
  text-decoration: none;
  /* GPU acceleration for better performance */
  transform: translateZ(0);
  will-change: transform;
}
.now-playing-image1 {
  width: 70%;
  cursor: pointer;
  display: none;
  object-fit: cover;
}
.now-playing-info {
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
}
.now-playing-text {
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  margin-bottom: 10px;
}
.now-playing-artist {
  color: #000000;
  font-size: var(--artist) !important;
  align-self: center;
  font-style: normal;
  margin-top: var(--dl-space-space-halfunit);
  font-family: Syne;
  font-weight: 600;
  line-height: 1.4;
  margin-left: 10px;
  margin-right: 10px;
  margin-bottom: 12px;
  text-align: center;
  word-wrap: break-word;
  overflow-wrap: break-word;
  hyphens: auto;
  max-width: 100%;
}
.now-playing-title {
  color: #000000;
  font-size: var(--title) !important;
  align-self: center;
  font-family: Syne;
  line-height: 1.4;
  margin-left: var(--dl-space-space-unit);
  margin-right: var(--dl-space-space-unit);
  margin-bottom: 12px;
  text-align: center;
  word-wrap: break-word;
  overflow-wrap: break-word;
  hyphens: auto;
  max-width: 100%;
}
.now-playing-album {
  color: #000000;
  font-size: var(--album) !important;
  align-self: center;
  font-style: italic;
  font-family: Syne;
  font-weight: 300;
  text-align: center;
  line-height: 0.8vw;
  margin-left: var(--dl-space-space-unit);
  margin-right: var(--dl-space-space-unit);
}
.now-playing-icons {
  justify-content: center;
  display: flex;
  flex-direction: row;
  margin-bottom: var(--dl-space-space-unit);
}

.now-playing-icon {
  padding-left: 10px;
  display: block;
  max-width: 30px;
  max-height: 30px;
  width: 2.25vmax;
  height: 2.25vmax;
}

/* .now-playing-discogslink {
  left: 0px;
  right: 0px;
  width: 25px;
  bottom: -5px;
  height: 30px;
  margin: auto;
  position: absolute;
} */

/* Error state styles */
.cover-button.error-state {
  opacity: 1 !important;
  background-color: rgba(255, 0, 0, 0.1);
  border: 2px solid rgba(255, 0, 0, 0.3);
}

.connection-error-indicator {
  position: absolute;
  top: 10px;
  left: 10px;
  background-color: rgba(255, 0, 0, 0.8);
  color: white;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  z-index: 10;
}

@media(max-width: 1600px) {

  .now-playing-artist {
    line-height: 2vw;
  }
  .now-playing-title {
    line-height: 1.3vw;
  }
  .now-playing-album {
    line-height: 1vw;
  }

  .now-playing-discogslink {
    top: -7px;
    left: 0px;
    right: 0px;
    margin: auto;
    position: absolute;
  }
}
@media(max-width: 1200px) {
  .now-playing-now-playing {
    width: 100%;
    height: 100%;
  }
  .now-playing-image-container {
    background-image: url('https://i.discogs.com/G7Y1ld1eBjQZ6_NjwpWbBVWyVWJYnLdC3Lnb8jdeLt4/rs:fit/g:sm/q:90/h:600/w:600/czM6Ly9kaXNjb2dz/LWRhdGFiYXNlLWlt/YWdlcy9SLTEwNDg5/MTc4LTE0OTg0OTY1/MDctODY4My5qcGVn.jpeg');
  }
  .now-playing-title {
    line-height: 1.7vw;
  }
  .now-playing-album {
    line-height: 1.5vw;
  }

}
@media(max-width: 991px) {
  .now-playing-now-playing {
    margin-left: 0px;
    margin-right: 0px;
  }
  .now-playing-header {
    left: 0px;
    right: 0px;
    margin: auto;
  }
  .now-playing-playing-track {
    width: 100%;
    margin-left: 0;
    margin-right: 0;
    justify-content: flex-start;
    background-color: #ffffff;
  }
  .now-playing-artist {
    color: #000000;
    align-self: center;
    margin-top: var(--dl-space-space-halfunit);
    font-family: Syne;
    line-height: 1.4;
    margin-bottom: 8px;
    word-wrap: break-word;
    overflow-wrap: break-word;
    hyphens: auto;
    max-width: 100%;
  }
  .now-playing-title {
    color: #000000;
    line-height: 1.4;
    margin-left: var(--dl-space-space-unit);
    margin-right: var(--dl-space-space-unit);
    margin-bottom: 8px;
    word-wrap: break-word;
    overflow-wrap: break-word;
    hyphens: auto;
    max-width: 100%;
  }
  .now-playing-album {
    color: #000000;
    line-height: 2vw;
    margin-left: var(--dl-space-space-unit);
    margin-right: var(--dl-space-space-unit);
  }

  .now-playing-icon {
    padding-left: 10px;
    display: block;
    width: 25px;
    height: 25px;
  }
}
@media(max-width: 767px) {
  .now-playing-playing-track {
    width: 100%;
    margin-right: 0px;
  }

}
@media(max-width: 479px) {
  .now-playing-track {
    padding-top: 4px;
    padding-left: 4px;
    padding-right: 4px;
  }
  .now-playing-artist {
    margin-top: 5px;
    line-height: 1.4;
  }

  .now-playing-title {
    line-height: 1.4;
  }

  .now-playing-album {
    line-height: 2.5vw;
  }
}


.hidden {
  display: none;
}

.cover-button {
    padding: 10%;
    position: absolute;
    height: 100%;
    width: 100%;
    margin-left: auto;
    object-fit: cover;
    margin-right: auto;
    left: 0;
    right: 0;
    text-align: center;
    z-index: 1000;
    background-size: cover;
    opacity: .5;
    cursor: pointer;
}

.cover-button:hover {
  opacity: 1;
}

.play-control{
  background-image: url("/playground_assets/play.png");
  transition: opacity 0.5s;
}
.pause-control {
  background-image: url("/playground_assets/pause.png");
  opacity: 0;
  transition: opacity 5s;  
}

.pause-control:hover {
  background-image: url("/playground_assets/pause.png");
  opacity: 1;
  transition: opacity 0.5s;  
}

/* Mobile styles - remove opacity animations since no hover state */
@media (max-width: 767px) {
  .cover-button {
    opacity: 1; /* Always visible on mobile */
  }
  
  .play-control {
    transition: none; /* No transition on mobile */
  }
  
  .pause-control {
    opacity: 1; /* Always visible on mobile */
    transition: none; /* No transition on mobile */
  }
  
  .cover-button:hover,
  .pause-control:hover {
    opacity: 1; /* No hover effect on mobile */
  }
}
</style>
